import { WORKSHOPS } from '@chukua/common';
import { Alert, Button, Form, Input, Select } from 'antd';
import { useGarageOptions } from '../../workshop-bookings/hooks/use-garage-options';
import { WORKSHOP_LABELS, type WorkshopFormValues } from '../policies/workshop-form.policy';

const WORKSHOP_OPTIONS = WORKSHOPS.map((workshop) => ({ value: workshop, label: WORKSHOP_LABELS[workshop] }));

interface WorkshopFormProps {
  initial: WorkshopFormValues;
  error: string | null;
  saving: boolean;
  onSave: (values: WorkshopFormValues) => Promise<void>;
  onCancel: () => void;
}

/** The garage picker opens with the active garages, fetched whole: the directory is a handful of workshops. A garage and a warranty only appear for external work. */
export function WorkshopForm({ initial, error, saving, onSave, onCancel }: WorkshopFormProps) {
  const [form] = Form.useForm<WorkshopFormValues>();
  const workshop = Form.useWatch('workshop', form);
  const garages = useGarageOptions();

  return (
    <section aria-label="Workshop" className="flex flex-col gap-3">
      <h2 className="m-0 text-base font-semibold">Workshop</h2>
      {error && <Alert type="error" title={error} showIcon />}
      <Form<WorkshopFormValues> form={form} layout="vertical" requiredMark={false} initialValues={initial} disabled={saving} onFinish={onSave}>
        <Form.Item name="workshop" label="Where is the work done?" rules={[{ required: true, message: 'Choose where the work is done' }]}>
          <Select options={WORKSHOP_OPTIONS} placeholder="Select a workshop" />
        </Form.Item>
        {workshop === 'garage' && (
          <Form.Item name="garageId" label="Garage" rules={[{ required: true, message: 'Choose the garage' }]}>
            <Select
              showSearch
              loading={garages === null}
              optionFilterProp="label"
              placeholder="Select or search garages"
              notFoundContent="No active garages"
              options={(garages ?? []).map((garage) => ({ value: garage.id, label: garage.name }))}
            />
          </Form.Item>
        )}
        <Form.Item name="odometerKm" label="Odometer (km)">
          <Input inputMode="numeric" />
        </Form.Item>
        <Form.Item name="symptoms" label="Symptoms">
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
        <Form.Item name="diagnosis" label="Diagnosis">
          <Input.TextArea rows={2} maxLength={4000} />
        </Form.Item>
        {workshop === 'garage' && (
          <Form.Item name="warrantyMonths" label="Warranty (months)" extra="Leave empty when the garage gives none.">
            <Input inputMode="numeric" />
          </Form.Item>
        )}
        <div className="flex gap-2">
          <Button type="primary" htmlType="submit" loading={saving}>
            Save workshop
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </div>
      </Form>
    </section>
  );
}
